// Tour step definitions — each step highlights a real element on a real page.
// The tour engine auto-navigates and waits for the target to mount.

export const TOUR_STEPS = [
  // ─── Chapter 1: Intro ─────────────────────────────────────────
  {
    chapter: 'Welcome',
    path: '/app/dashboard',
    target: null,           // full-page slide, no highlight
    title: 'Welcome to Iris Realty',
    body: 'The institutional property OS for Dubai & London. Over the next ~30 min I will walk you through every core module using the real app. Press Next to begin, or Esc to exit anytime.',
    hint: 'Every stat you see is live — pulled from Bayut, DLD, Emirates Auction.',
  },
  {
    chapter: 'Welcome',
    path: '/app/dashboard',
    target: '[data-testid="workspace-switcher"], .workspace-toggle, [aria-label*="workspace"]',
    title: 'Live vs Demo workspaces',
    body: 'Every user has two workspaces. Live = real Bayut/DLD data, real assets. Demo = seeded fake data safe for training. Toggle any time.',
    hint: 'Data never crosses between workspaces.',
    fallback: 'top-right',
  },

  // ─── Chapter 2: Portfolio ─────────────────────────────────────
  {
    chapter: 'Portfolio',
    path: '/app/properties',
    target: '[data-testid="properties-kanban"], .kanban-board, [data-testid="properties-list"]',
    title: 'Portfolio Pipeline',
    body: '6 stages: Sourcing → Under Offer → Acquired → In Refurb → Marketing → Sold. Cards show live ROI, days-on-market, and cost-overrun colour code.',
    hint: 'Green = on-plan, amber = at risk, red = overrun.',
  },
  {
    chapter: 'Portfolio',
    path: '/app/properties',
    target: '[data-testid="property-card"], .property-card, .kanban-card',
    title: 'Every asset — one page',
    body: 'Click any property to open its detail view: title deed, BOQ v1/v2/v3, all POs, invoices, snags, drawings, permits, and the AI verdict at time of purchase.',
    hint: 'Full audit trail — who uploaded what, when.',
  },

  // ─── Chapter 3: AI Deal Analyzer ──────────────────────────────
  {
    chapter: 'AI Deal Analyzer',
    path: '/app/deal-analyzer',
    target: '[data-testid="deal-url-input"], input[placeholder*="Bayut"], input[type="url"]',
    title: 'Paste a URL, get an institutional verdict',
    body: 'Drop any Bayut / PropertyFinder / Emirates Auction URL. Iris scrapes the listing, computes 20+ signals, runs 4 hard guards + 6 institutional guards, and returns STRONG BUY / BUY / HOLD / AVOID in ~15s.',
    hint: 'Uses Claude Sonnet 4.6 with agentic tool-calling.',
  },
  {
    chapter: 'AI Deal Analyzer',
    path: '/app/deal-analyzer',
    target: null,
    title: 'The 6 institutional guards',
    body: 'G1 same-building comps · G2 rent-yield ceiling · G3 age-price coherence · G4 DOM sanity · G5 365d transaction depth · G6 off-plan discount. Every guard shows the exact number that triggered pass/fail.',
    hint: 'G5 just went live after the DLD 5-year seed (498K txns).',
  },

  // ─── Chapter 4: Market Intelligence ───────────────────────────
  {
    chapter: 'Market Intelligence',
    path: '/app/daily-digest',
    target: null,
    title: 'Morning Brief',
    body: 'Every 6 AM Dubai time, Iris digests overnight changes: new high-score listings, price cuts on watchlist items, auction lots ending, community psf moves >2%.',
    hint: 'Written by the Iris agent — Claude reads deltas and drafts 4 paragraphs.',
  },
  {
    chapter: 'Market Intelligence',
    path: '/app/market',
    target: null,
    title: 'Trends — market at a glance',
    body: 'Median AED/sqft, DOM, sale volume, off-plan share — all by community. Filterable by bed count, size band, date window.',
    hint: 'Rolling 90-day median smoothed over the raw daily curve.',
  },
  {
    chapter: 'Market Intelligence',
    path: '/app/market/transactions',
    target: '[data-testid="transactions-browser-page"]',
    title: 'Transactions Browser (NEW)',
    body: '498,000 DLD sales from 2019-2024, just imported from Dubai Pulse. Filter by community, date, price, size, off-plan, bedrooms. Export CSV. Community names normalized — Emirates Hills catches First/Second/Third.',
    hint: 'Median AED/sqft recomputed for whatever filter you set.',
  },
  {
    chapter: 'Market Intelligence',
    path: '/app/market/live',
    target: '[data-testid="live-listings-page"]',
    title: 'Live Listings',
    body: 'Bayut + PropertyFinder refreshed every 6h, cross-source dedup\'d via a fingerprint field. Every listing carries an Iris score. Hottest first.',
    hint: 'Hotness = f(price-cut history, DOM, comp discount, recency).',
  },

  // ─── Chapter 5: Auctions ──────────────────────────────────────
  {
    chapter: 'Auctions',
    path: '/app/auctions',
    target: null,
    title: 'Auction alpha',
    body: 'Emirates Auction scraped every 6h via Apify. Bank-repossessed & court-ordered sales, often 15-30% below Bayut. Every lot runs the full Iris pipeline.',
    hint: 'Never bid without opening the Explainer — even auctions.',
  },

  // ─── Chapter 6: Ops ───────────────────────────────────────────
  {
    chapter: 'Operations',
    path: '/app/approvals',
    target: null,
    title: 'Approvals workflow',
    body: 'Every invoice above threshold needs Accountant → PM → MD signatures. Rejections require a reason. Full audit trail. Slack + email notifications on route.',
    hint: 'Default threshold AED 50K — configurable per workspace.',
  },
  {
    chapter: 'Operations',
    path: '/app/cash-flow',
    target: null,
    title: 'Cash Flow — real, not projected',
    body: 'Rolling 90-day inflow (buyer payments) vs outflow (vendor payments), by property. Pulled from actual bank feed + PO ledger. Alerts when runway < 30d.',
    hint: 'Retention: 10% held per vendor, released on snag close-out.',
  },

  // ─── Chapter 7: Admin ─────────────────────────────────────────
  {
    chapter: 'Admin',
    path: '/app/admin/sync-runs',
    target: null,
    title: 'Sync Runs Dashboard',
    body: '9 data pipelines, one screen. Traffic-light health per job, "Trigger now" on the safe-to-fire jobs, chronological run history below with error snippets.',
    hint: 'If a data source is stale > 48h a banner shows on every page.',
  },

  // ─── Chapter 8: Wrap ──────────────────────────────────────────
  {
    chapter: 'Wrap',
    path: '/app/dashboard',
    target: null,
    title: 'That\'s Iris Realty',
    body: 'Every deal, every drawing, every dirham — under one glass roof. Roadmap next: Fingerprint match (Bayut ↔ DLD same-building), yearly heatmap trendlines, watchlist alerts, consensus UI (Gemini alongside Claude).',
    hint: 'Access via workspace invite. Support at /app/help/*.',
  },
];
